import { useState } from 'react'
import type { PatchCategoryTranslation } from './endpoints/spend-control'

type TranslationEntry = { translationTag: string; name?: string; description?: string }
type TranslationField = { name: string; description: string }
type LocaleBlurred = { name: boolean; description: boolean }
type LocaleFieldErrors = { name?: string; description?: string }

export const useTranslationsFields = (
  loadedTranslations?: TranslationEntry[],
  { requireDescription = true }: { requireDescription?: boolean } = {},
) => {
  const [overrides, setOverrides] = useState<Record<string, TranslationField>>({})
  const [blurred, setBlurred] = useState<Record<string, LocaleBlurred>>({})

  const locales = loadedTranslations?.map((t) => t.translationTag) ?? []

  const findLoaded = (locale: string) => loadedTranslations?.find((t) => t.translationTag === locale)

  const getField = (locale: string): TranslationField => {
    const override = overrides[locale]
    if (override !== undefined) return override
    const loaded = findLoaded(locale)
    return { name: loaded?.name ?? '', description: loaded?.description ?? '' }
  }

  const translationFields = Object.fromEntries(locales.map((locale) => [locale, getField(locale)])) as Record<string, TranslationField>

  const updateTranslation = (locale: string, field: 'name' | 'description', value: string) => {
    setOverrides((prev) => {
      const loaded = findLoaded(locale)
      const base = prev[locale] ?? { name: loaded?.name ?? '', description: loaded?.description ?? '' }
      return { ...prev, [locale]: { ...base, [field]: value } }
    })
  }

  const markBlurred = (locale: string, field: 'name' | 'description') => {
    setBlurred((prev) => ({
      ...prev,
      [locale]: { ...(prev[locale] ?? { name: false, description: false }), [field]: true },
    }))
  }

  const isNewLocale = (locale: string): boolean => {
    const loaded = findLoaded(locale)
    return !((loaded?.name ?? '').trim() || (loaded?.description ?? '').trim())
  }

  const getLocaleValidationError = (locale: string): LocaleFieldErrors => {
    const f = getField(locale)
    const loaded = findLoaded(locale)
    if (isNewLocale(locale)) {
      const hasName = f.name.trim().length > 0
      const hasDescription = f.description.trim().length > 0
      if (requireDescription && hasName && !hasDescription) return { description: 'Description is required' }
      if (!hasName && hasDescription) return { name: 'Name is required' }
      return {}
    }
    const errors: LocaleFieldErrors = {}
    if ((loaded?.name ?? '').trim() && !f.name.trim()) errors.name = 'Name is required'
    if (requireDescription && (loaded?.description ?? '').trim() && !f.description.trim()) errors.description = 'Description is required'
    return errors
  }

  const hasTranslationsValidationErrors = locales.some((locale) => Object.keys(getLocaleValidationError(locale)).length > 0)

  const translationsValidationErrors = Object.fromEntries(
    locales.map((locale) => {
      const errors = getLocaleValidationError(locale)
      const localeBlurred = blurred[locale] ?? { name: false, description: false }
      const f = getField(locale)
      const displayed: LocaleFieldErrors = {}
      if (isNewLocale(locale)) {
        if (errors.description && localeBlurred.name && f.name.trim().length > 0) displayed.description = errors.description
        if (errors.name && localeBlurred.description && f.description.trim().length > 0) displayed.name = errors.name
      } else {
        if (errors.name && localeBlurred.name) displayed.name = errors.name
        if (errors.description && localeBlurred.description) displayed.description = errors.description
      }
      return [locale, displayed]
    }),
  ) as Record<string, LocaleFieldErrors>

  const buildPatchPayload = (): PatchCategoryTranslation[] =>
    locales.flatMap((locale) => {
      const f = getField(locale)
      const loaded = findLoaded(locale)
      const currentName = f.name.trim()
      const currentDescription = f.description.trim()
      const initialName = (loaded?.name ?? '').trim()
      const initialDescription = (loaded?.description ?? '').trim()
      if (currentName === initialName && currentDescription === initialDescription) return []
      if (!currentName && !currentDescription) return []
      const t: { translationTag: string; name?: string; description?: string } = { translationTag: locale }
      if (currentName) t.name = currentName
      if (currentDescription) t.description = currentDescription
      return [t as PatchCategoryTranslation]
    })

  const isTranslationsChanged = buildPatchPayload().length > 0

  return {
    translationFields,
    updateTranslation,
    markBlurred,
    buildPatchPayload,
    translationsValidationErrors,
    hasTranslationsValidationErrors,
    isTranslationsChanged,
  }
}
